/**
 * CanvasForm.js
 * Created by vikas on 04/27/2020.
 */

import React, {useState, useEffect, useContext, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {actionIcBuild24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {IconoirCustomMoreVerticalAlt} from '@ey-xd/motif-icon';
import _ from 'lodash';
import {labels, normalize} from '../../../util/utils';
import Button from '../BaseComponentWrappers/MotifButton';
import ConfirmationModal from '../Modal/Modal';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import SectionList from './Section/SectionList';
import Icon from '../BaseComponentWrappers/MotifIcon';
import IconButton from '../BaseComponentWrappers/MotifIconButton';
import Dropdown from '../BaseComponentWrappers/MotifDropdown';
import EllipseControl from '../EllipsesControl/EllipsesControl';
import {loadStorage, saveStorage} from '../localStorage';
import Validation from './Common/Validation';
import {
	clientSideEventNames,
	documentTypes,
	entities,
	serviceURL,
	canvasFormBannerTypes
} from '../../../util/uiconstants';
import {DocumentEngagementIdContext} from '../../Main/Forms/CanvasFormContainer';
import IndependenceModal from '../CanvasForm/IndependenceModal/IndependenceModal';
import CanvasBanner from './Banner/CanvasBanner';
import {deleteHeader} from '../../../actions/FormData/formdataactions';
import {clearFormBodyDisplayData, clearFormBodySaveState} from '../../../actions/Form/formBodyActions';
import {anyBodyHasUnsavedChanges} from '../../../selectors/Forms/formBodySelectors';
import env from '../../../util/env';
import {useClearAllComments, useDocumentId, useEngagementId, useUserPreferences} from '../../../util/customHooks';
import AddEditCustomModal from '../../Main/Forms/AddEditCustom/AddEditCustomModal';
import DraggableComments from '../EngagementComment/DraggableComments';
import DraggableGuidance from './Bodies/Common/DraggableGuidance';
import BannerMessage from '../BannerMessage/BannerMessage';
import $ from 'jquery';
import UnsavedChangesModal from '../UnsavedChangesModal/UnsavedChangesModal';
import {getTaskDocumentByDocumentId} from '../../../selectors/Ares/selectors';

let filteredSections = [];
let currentHeader = undefined;
let selectedSection = undefined;

export default function CanvasForm(props) {
	const {formData, documentTypeId, forceReload, setShowLoader, isFiltered} = props;

	const [isOpen, setIsOpen] = useState(false),
		[sharedMessageVisible, setSharedMessageVisible] = useState(false),
		[showIndependenceLoading, setShowIndependenceLoading] = useState(false),
		[showModal, setShowModal] = useState(false),
		[showEditModal, setShowEditModal] = useState(false),
		[commentsCleared, setCommentsCleared] = useState(false),
		[modalType, setModalType] = useState(undefined),
		[showCommentsModal, setShowCommentsModal] = useState(false),
		[showGuidanceModal, setShowGuidanceModal] = useState(false),
		[bodyId, setBodyId] = useState(undefined),
		[commentsDocumentTypeId, setCommentsDocumentTypeId] = useState(undefined),
		[isExternallyShared, setIsExternallyShared] = useState(false),
		[guidanceDetails, setGuidanceDetails] = useState([]),
		[isDeleteHeader, setIsDeleteHeader] = useState(false),
		[nextSectionName, setNextSectionName] = useState(null),
		[previousSectionName, setPreviousSectionName] = useState(null),
		[render, setRender] = useState(true),
		[guidanceId, setGuidanceId] = useState(0),
		[showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

	const documentEngagementId = useContext(DocumentEngagementIdContext),
		documentId = useDocumentId(),
		userPreferences = useUserPreferences(),
		engagementId = useEngagementId();

	const noSectionsAvailable = useMemo(() => {
		return !(formData?.headers && formData?.headers.length > 0 && formData.headers.some((s) => s.sections?.length > 0));
	}, [formData]);

	const selectedHeaderIdKey = useMemo(() => `selectedHeaderId-${documentId}`, [documentId]);

	const signOffs = useSelector((store) => store.signOffs),
		doc = useSelector((state) => getTaskDocumentByDocumentId(state, documentId)),
		engagement = useSelector((store) => store.engagement),
		submitProfileDetails = useSelector((store) => store.submitProfileDetails);

	const hasUnsavedChanges = useSelector((store) => anyBodyHasUnsavedChanges(store));

	const unsavedChangesCallbackData = useRef();

	const [showInfoBanner, setShowInfoBanner] = useState(documentTypeId === documentTypes.INDEPENDENCEFORMTEMPLATE);
	const {setClearAllComments} = useClearAllComments(
		engagementId,
		documentId,
		documentTypeId,
		entities.FormHeader,
		bodyId,
		() => {
			setClearAllComments(false);
			setCommentsCleared(true);
			setShowIndependenceLoading(false);
		}
	);

	let bodyOnlyView = true;
	let validHeaders = [];

	if (window.location.pathname.toLowerCase().includes('changes')) {
		bodyOnlyView = false;
	}

	const dispatch = useDispatch();

	const setSelectedSection = (section) => {
		selectedSection = section;
	};

	const getHeaderListForView = (data) => {
		let formattedHeaderList = [];
		if (data && data.headers && data.headers.length > 0) {
			formattedHeaderList = data.headers.map((h) => {
				if (
					h.sections?.filter((s) => s.isDisplayed === true || s.isCustom === true).length > 0 ||
					h.isCustom === true
				) {
					return {
						id: h.id,
						value: h.id,
						label: h.name,
						title: h.name,
						isCustom: h.isCustom,
						knowledgeDisplayOrder: h.knowledgeDisplayOrder
					};
				} else {
					return {};
				}
			}, this);
		}
		return formattedHeaderList;
	};

	const getHeaderGivenSectionId = (data, sectionId) => {
		return data?.headers.find((h) => {
			return _.find(h.sections, {id: sectionId});
		});
	};

	const findSectionForHeader = (id, data, header, isHeaderChanged = false, isRailChange = false) => {
		const associatedHeader = data?.headers.find((fl) => {
			if (header && header.value) {
				if (fl.id === header.value) {
					return fl.sections;
				}
			} else if (header && header.id) {
				if (fl.id === header.id) {
					return fl.sections;
				}
			}
		});
		let displayedSections = getDisplayedSections(associatedHeader);
		if (displayedSections && displayedSections.length > 0) {
			const normalized = normalize(displayedSections);
			filteredSections = normalized.allIds.map((sectionId) => {
				return normalized.byId[sectionId];
			});
			if (bodyOnlyView === true) {
				if (isRailChange) {
					let index = filteredSections.findIndex((fs) => fs.id === id);
					if (index < filteredSections.length) {
						nextOrPreviousInCurrentHeader(
							id ? id : normalized.allIds[index],
							true,
							index >= 0 ? index : 0,
							isRailChange
						);
					} else {
						const currentHeaderIndex = formData.headers.findIndex((h) => h.id === currentHeader?.id);
						if (currentHeaderIndex >= 0) {
							findHeaderOrSection(id, true, currentHeaderIndex, true);
						}
					}
					window.dispatchEvent(
						new CustomEvent(clientSideEventNames.onFormChange, {
							detail: {id: id ? id : normalized.allIds[index], level: 'level2', headerId: header.id}
						})
					);
				}
			}
			if (isHeaderChanged === true) {
				if (normalized.byId[id]) {
					window.dispatchEvent(
						new CustomEvent(clientSideEventNames.onFormChange, {
							detail: {id: id, level: 'level2', headerId: header.id}
						})
					);
				} else {
					window.dispatchEvent(
						new CustomEvent(clientSideEventNames.onFormChange, {
							detail: {id: normalized.byId[normalized.allIds[0]].id, level: 'level2', headerId: header.id}
						})
					);
				}
			}
		} else {
			filteredSections = [];
		}
	};

	const setClickOutside = () => {
		setIsOpen(false);
	};

	const handleToggle = () => {
		setIsOpen(true);
	};

	const onRailChange = (event) => {
		selectedSection = undefined;
		if (event.detail.isDeleteHeader) {
			setIsDeleteHeader(true);
			return;
		}
		const id = event.detail.id,
			level = event.detail.level,
			data = event.detail.data;
		if (level === 'level1') {
			const headers = getHeaderListForView(data);
			const header = headers.filter((h) =>
				loadStorage(selectedHeaderIdKey) !== undefined ? h.value === loadStorage(selectedHeaderIdKey) : h.value === id
			);
			currentHeader = header[0];
			saveStorage(selectedHeaderIdKey, loadStorage(selectedHeaderIdKey) || currentHeader?.id);
			findSectionForHeader(id, data, currentHeader, true, true);
			if (filteredSections.length > 0) {
				selectedSection = filteredSections[0];
			}
			if (filteredSections.length === 0) {
				if (validHeaders.length === 0) {
					getValidHeaders();
				}
				const currentHeaderIndex = validHeaders.findIndex((h) => h.id === currentHeader?.id);
				const nextHeader = validHeaders[currentHeaderIndex + 1],
					previousHeader = validHeaders[currentHeaderIndex - 1];

				setNext(nextHeader);
				const previousDisplayedSections = getDisplayedSections(previousHeader);
				if (previousDisplayedSections && previousDisplayedSections.length > 0) {
					setPrevious(previousDisplayedSections[previousDisplayedSections.length - 1]);
				} else {
					setPrevious(previousHeader);
				}
			} else {
				const currentHeaderIndex = validHeaders.findIndex((h) => h.id === currentHeader?.id);
				const previousHeader = headers[currentHeaderIndex - 1];

				if (currentHeader.sections?.length > 1) {
					setNext(currentHeader.sections[1]);
				} else {
					setNext(validHeaders[currentHeaderIndex + 1]);
				}
				setPrevious(previousHeader);
			}
		} else if (level === 'level2') {
			/*find the header for this sectionId*/
			const header = getHeaderGivenSectionId(data, id);
			if (header) {
				let temp = {
					id: header.id,
					value: header.id,
					label: header.name,
					title: header.name,
					isCustom: header.isCustom,
					knowledgeDisplayOrder: header.knowledgeDisplayOrder,
					knowledgeFormHeaderId: header.knowledgeFormHeaderId,
					sections: header.sections
				};
				currentHeader = temp;
				saveStorage(selectedHeaderIdKey, temp.value);
				findSectionForHeader(id, data, temp, false, true);
			}
		}
	};

	const handleClickDropdownItem = (type) => {
		setModalType(type);
		switch (type) {
			case 'delete':
				setShowModal(!showModal);
				break;
			case 'edit': {
				setShowEditModal(!showEditModal);
				break;
			}
		}
	};

	const confirmActionClick = () => {
		setShowModal(false);
		setShowLoader(true);
		dispatch(
			deleteHeader(env.getBaseURL(serviceURL, engagementId) + `forms/${documentId}/headers/${currentHeader?.id}`, {
				documentId: documentId,
				headerId: currentHeader?.id
			})
		)
			.then(() => {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.onCreateOrEdit, {
						detail: {signOffs: signOffs, engagement: {engagementId: engagementId}}
					})
				);

				setShowLoader(false);
				saveStorage(selectedHeaderIdKey, undefined);
			})
			.catch(() => {
				setShowModal(false);
				setShowLoader(false);
			});
	};

	const cancel = () => {
		if (modalType === 'delete') {
			setShowModal(false);
		} else {
			setShowEditModal(false);
		}
	};

	const showComments = (event) => {
		setBodyId(event.detail.bodyId);
		setShowCommentsModal(event.detail.show);
		setCommentsDocumentTypeId(event.detail.documentTypeId);
	};

	const closeCommentsModal = () => {
		setShowCommentsModal(false);
		window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
		$('span[id^=cmtIcon]').removeClass('active');
		$('.comments-icon.active').removeClass('active');
	};

	const openGuidanceModal = (event) => {
		setShowGuidanceModal(event.detail.show);
		setGuidanceDetails(event.detail.guidanceDetails);
		setGuidanceId(event.detail.guidanceId);
	};

	const closeGuidanceModal = () => {
		setShowGuidanceModal(false);
	};

	const setPreviousToPreviousHeader = (header) => {
		const previousDisplayedSections = getDisplayedSections(header);
		setPrevious(
			previousDisplayedSections.length > 0 ? previousDisplayedSections[previousDisplayedSections.length - 1] : header
		);
	};

	const getValidHeaders = () => {
		validHeaders = formData?.headers?.filter((h) => {
			const hasSections = h.sections?.filter((s) => s.isDisplayed === true || s.isCustom === true);
			if (hasSections && (hasSections.length > 0 || h.isCustom === true)) {
				return h;
			} else {
				return undefined;
			}
		});
	};

	const getDisplayedSections = (header) => {
		return header?.sections?.filter((s) => s.isDisplayed === true || s.isCustom === true) || [];
	};

	const findHeaderOrSection = (next, currentHeaderIndex, isRailChange = false) => {
		let header = undefined;
		if (validHeaders.length === 0) {
			getValidHeaders();
		}
		if (next) {
			header = validHeaders[currentHeaderIndex + 1];
		} else {
			header = validHeaders[currentHeaderIndex - 1];
		}

		if (header && header.sections.length > 0) {
			if (isRailChange) {
				if (next) {
					setNext(header);
				} else {
					setPrevious(header);
				}
			}
			findSectionForHeader(undefined, formData, header, true, false);
			if (filteredSections.length === 0) {
				currentHeader = validHeaders[currentHeaderIndex];
				/*no sections for current header. set next to next header, previous to last section in previous header*/
				setPreviousToPreviousHeader();
				if (validHeaders[currentHeaderIndex + 1]) {
					setNext(validHeaders[currentHeaderIndex + 1]);
				} else {
					setNext(null);
				}
				selectedSection = undefined;
				filteredSections = [];
				if (validHeaders[currentHeaderIndex + 1]) {
					currentHeader = validHeaders[currentHeaderIndex + 1];
					window.dispatchEvent(
						new CustomEvent(clientSideEventNames.onFormChange, {
							detail: {
								id: currentHeader.id,
								level: 'level1',
								headerId: currentHeader.id
							}
						})
					);
				}
			}
			if (filteredSections.length > 0) {
				currentHeader = header;
				if (next) {
					selectedSection = filteredSections[0];
					setNext(
						filteredSections[1]
							? filteredSections[1]
							: validHeaders[currentHeaderIndex + 2]
							? validHeaders[currentHeaderIndex + 2]
							: null
					);
					setPreviousToPreviousHeader(validHeaders[currentHeaderIndex]);
				} else {
					let previousDisplayedSections = getDisplayedSections(currentHeader);
					if (previousDisplayedSections.length >= 0) {
						selectedSection = previousDisplayedSections[previousDisplayedSections.length - 1];
						setNext(validHeaders[currentHeaderIndex]);
					} else {
						setNext(null);
					}

					if (previousDisplayedSections.length === 1) {
						previousDisplayedSections = getDisplayedSections(validHeaders[currentHeaderIndex - 2]);
						if (previousDisplayedSections) {
							if (previousDisplayedSections.length > 0) {
								setPrevious(previousDisplayedSections[previousDisplayedSections.length - 1]);
							} else {
								setPrevious(validHeaders[currentHeaderIndex - 2]);
							}
						} else {
							setPrevious(null);
						}
					} else {
						setPrevious(
							previousDisplayedSections[previousDisplayedSections.length - 2]
								? previousDisplayedSections[previousDisplayedSections.length - 2]
								: validHeaders.findIndex((h) => h.id === currentHeader.id) === 0
								? null
								: currentHeader
						);
					}
					window.dispatchEvent(
						new CustomEvent(clientSideEventNames.onFormChange, {
							detail: {
								id: selectedSection?.id,
								level: 'level2',
								headerId: currentHeader?.id
							}
						})
					);
				}
			}
		} else {
			/*no sections for this header*/
			selectedSection = undefined;
			if (next) {
				currentHeader = validHeaders[currentHeaderIndex + 1];
				if (validHeaders[currentHeaderIndex + 2]) {
					setNext(validHeaders[currentHeaderIndex + 2]);
					let previousDisplayedSections = getDisplayedSections(validHeaders[currentHeaderIndex]);
					if (previousDisplayedSections.length > 0) {
						setPrevious(previousDisplayedSections[previousDisplayedSections.length - 1]);
					} else {
						setPrevious(validHeaders[currentHeaderIndex]);
					}
				} else {
					setNext(null);
					setPrevious(validHeaders[currentHeaderIndex]);
				}
			} else {
				currentHeader = validHeaders[currentHeaderIndex - 1];
				setNext(validHeaders[currentHeaderIndex]);
				let previousDisplayedSections = getDisplayedSections(validHeaders[currentHeaderIndex - 1]);
				if (previousDisplayedSections.length > 0) {
					setPrevious(previousDisplayedSections[previousDisplayedSections.length - 1]);
				} else {
					previousDisplayedSections = getDisplayedSections(validHeaders[currentHeaderIndex - 2]);
					if (previousDisplayedSections.length > 0) {
						setPrevious(previousDisplayedSections[previousDisplayedSections.length - 1]);
					} else {
						setPrevious(validHeaders[currentHeaderIndex - 2] ? validHeaders[currentHeaderIndex - 2] : null);
					}
				}
			}
			filteredSections = [];
			window.dispatchEvent(
				new CustomEvent(clientSideEventNames.onFormChange, {
					detail: {
						id: currentHeader.id,
						level: 'level1',
						headerId: currentHeader.id
					}
				})
			);
		}
	};

	const setNext = (headerOrSection) => {
		if (headerOrSection) {
			setNextSectionName(
				headerOrSection.customLabel && true && headerOrSection.customLabel.trim().length > 0
					? headerOrSection.name + ' - ' + headerOrSection.customLabel
					: headerOrSection.name
			);
		} else {
			setNextSectionName(null);
		}
	};

	const setPrevious = (headerOrSection) => {
		if (headerOrSection) {
			setPreviousSectionName(
				headerOrSection.customLabel && true && headerOrSection.customLabel.trim().length > 0
					? headerOrSection.name + ' - ' + headerOrSection.customLabel
					: headerOrSection.name
			);
		} else {
			setPreviousSectionName(null);
		}
	};

	const nextOrPreviousHeader = (currentHeaderIndex, previousHeader, nextHeader, next, isRailChange = false) => {
		if (nextHeader) {
			if (next === true) {
				if (nextHeader.isCustom || getDisplayedSections(nextHeader).length > 0) {
					setNext(nextHeader);
				} else {
					setNext(null);
				}
				setPrevious(previousHeader);
			} else {
				const previousDisplayedSections = getDisplayedSections(previousHeader);
				if (previousDisplayedSections.length > 0) {
					setPrevious(previousDisplayedSections[previousDisplayedSections.length - 1]);
				} else {
					setPrevious(
						previousDisplayedSections.length === 0
							? currentHeaderIndex > 1 && getDisplayedSections(formData.headers[currentHeaderIndex - 1]).length === 0
								? formData.headers[currentHeaderIndex - 1]
								: null
							: previousHeader
					);
				}
			}
			if (isRailChange === false) {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.onFormChange, {
						detail: {
							id: nextHeader.id,
							level: 'level1',
							headerId: nextHeader.id
						}
					})
				);
			}
		} else {
			setNext(null);
			const previousDisplayedSections = getDisplayedSections(previousHeader);
			if (previousDisplayedSections) {
				if (previousDisplayedSections.length > 0) {
					setPrevious(previousDisplayedSections[previousDisplayedSections.length - 1]);
				} else {
					setPrevious(previousDisplayedSections.length === 0 ? null : previousHeader);
				}
			} else {
				setPrevious(null);
			}
		}
	};

	const nextOrPreviousInCurrentHeader = (id, isNext = null, index = null, isRailChange = false) => {
		if (isNext !== null) {
			/*clicked on next or previous button*/
			let section = filteredSections.find((fs) => fs.id === id);
			if (section) {
				selectedSection = section;
				if (filteredSections[index + 1]) {
					setNext(filteredSections[index + 1]);
				} else {
					const currentHeaderIndex = formData?.headers?.findIndex((h) => h.id === currentHeader?.id);
					if (validHeaders.length === 0) {
						getValidHeaders();
					}
					const nextHeader = validHeaders[currentHeaderIndex + 1];
					nextOrPreviousHeader(
						currentHeaderIndex,
						currentHeaderIndex === 1
							? getDisplayedSections(validHeaders[0]).length > 0
								? validHeaders[0]
								: null
							: currentHeader,
						nextHeader,
						true,
						isRailChange
					);
				}

				if (filteredSections[index - 1]) {
					setPrevious(filteredSections[index - 1]);
				} else {
					const currentHeaderIndex = formData.headers.findIndex((h) => h.id === currentHeader?.id);
					let previousHeader = formData.headers[currentHeaderIndex - 1];
					if (
						!previousHeader ||
						previousHeader.sections?.filter((s) => s.isDisplayed === true || s.isCustom === true).length <= 0
					) {
						if (formData.headers[currentHeaderIndex - 2]) {
							nextOrPreviousHeader(
								currentHeaderIndex,
								formData.headers[currentHeaderIndex - 2],
								currentHeader,
								false,
								isRailChange
							);
						} else {
							setPrevious(null);
						}
					} else {
						nextOrPreviousHeader(currentHeaderIndex, previousHeader, currentHeader, false, isRailChange);
					}
				}
			} else {
				const currentHeaderIndex = formData.headers.findIndex((h) => h.id === currentHeader?.id);
				const nextHeader = formData.headers[currentHeaderIndex + 1];
				nextOrPreviousHeader(currentHeaderIndex, currentHeader, nextHeader, true, isRailChange);
			}
			if (isRailChange === false) {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.onFormChange, {
						detail: {id: filteredSections[index].id, level: 'level2', headerId: currentHeader?.id}
					})
				);
			}
		}
	};

	const onPrevious = (id) => {
		if (hasUnsavedChanges) {
			unsavedChangesCallbackData.current = {
				callback: performOnPrevious,
				id: id
			};
			setShowUnsavedChangesModal(true);
		} else {
			performOnPrevious(id);
		}
	};

	const performOnPrevious = (id) => {
		let index = filteredSections.findIndex((fs) => fs.id === id);
		if (filteredSections[index - 1]) {
			nextOrPreviousInCurrentHeader(filteredSections[index - 1].id, false, index - 1);
		} else {
			if (validHeaders.length === 0) {
				getValidHeaders();
			}
			const currentHeaderIndex = validHeaders.findIndex((h) => h.id === currentHeader?.id);
			if (currentHeaderIndex >= 0) {
				findHeaderOrSection(false, currentHeaderIndex);
			}
		}
	};

	const onNext = (id) => {
		if (hasUnsavedChanges) {
			unsavedChangesCallbackData.current = {
				callback: performOnNext,
				id: id
			};
			setShowUnsavedChangesModal(true);
		} else {
			performOnNext(id);
		}
	};

	const performOnNext = (id) => {
		let index = filteredSections.findIndex((fs) => fs.id === id);
		if (filteredSections[index + 1]) {
			nextOrPreviousInCurrentHeader(filteredSections[index + 1].id, true, index + 1);
		} else {
			if (validHeaders.length === 0) {
				getValidHeaders();
			}
			const currentHeaderIndex = validHeaders.findIndex((h) => h.id === currentHeader?.id);
			if (currentHeaderIndex >= 0) {
				findHeaderOrSection(true, currentHeaderIndex);
			}
		}
	};

	const onSectionChange = (id, callback) => {
		dispatch(clearFormBodyDisplayData()).then(() => {
			callback && callback(id);
		});
	};

	//#region Unsaved changes modal callbacks
	const onUnsavedChangesStay = () => {
		setShowUnsavedChangesModal(false);
	};

	const onUnsavedChangesLeave = () => {
		dispatch(clearFormBodySaveState()).then(() => {
			const {callback, id} = unsavedChangesCallbackData.current;
			callback && callback(id);

			// 3. Hide the modal.
			setShowUnsavedChangesModal(false);
			unsavedChangesCallbackData.current = undefined;
		});
	};
	//#endregion

	const showSectionList = useMemo(() => {
		return (
			<SectionList
				bodyOnlyView={bodyOnlyView}
				forceReload={forceReload}
				formData={formData}
				sections={filteredSections}
				headerId={currentHeader?.id}
				header={currentHeader}
				hasHeaders={formData ? formData?.headers?.length > 0 : false}
				setSelectedSection={setSelectedSection}
				selectedSection={selectedSection}
				isSnapshotView={false}
				commentsCleared={commentsCleared}
				nextName={nextSectionName}
				previousName={previousSectionName}
				onNext={(id) => onSectionChange(id, onNext)}
				onPrevious={(id) => onSectionChange(id, onPrevious)}
				isFiltered={isFiltered}
				isDeleteHeader={isDeleteHeader}
			/>
		);
	}, [
		bodyOnlyView,
		commentsCleared,
		forceReload,
		formData,
		isFiltered,
		nextSectionName,
		onNext,
		onPrevious,
		onSectionChange,
		previousSectionName
	]);

	const displayBanner = () => {
		let bannerDisplayFunc = () => {};

		switch (documentTypeId) {
			case documentTypes.INDEPENDENCEFORMTEMPLATE:
				bannerDisplayFunc = () => {
					return (
						<BannerMessage
							variant={'warning'}
							message={labels.independenceViewTemplateMessage}
							onClose={() => {
								setShowInfoBanner(false);
							}}
						/>
					);
				};
				break;
			default:
				break;
		}

		return bannerDisplayFunc();
	};

	useEffect(() => {
		const headerList = getHeaderListForView(formData);
		let filteredHeader =
			loadStorage(selectedHeaderIdKey) !== undefined
				? headerList.filter((h) => h.value === loadStorage(selectedHeaderIdKey))[0]
				: headerList[0];
		currentHeader = filteredHeader;
		if (filteredHeader !== undefined) {
			setRender(!render);
			findSectionForHeader(
				selectedSection ? selectedSection.id : undefined,
				formData,
				filteredHeader,
				loadStorage(selectedHeaderIdKey) !== undefined ? false : true,
				true
			);
			if (!selectedSection || selectedSection.formHeaderId !== filteredHeader.id) {
				if (filteredSections.length > 0) {
					selectedSection = filteredSections[0];
				}
			}
		} else {
			filteredSections = [];
			selectedSection = undefined;
			setRender(!render);
		}
		if (
			formData &&
			formData.headers &&
			formData.headers.length === 1 &&
			formData.headers[0].sections &&
			formData.headers[0].sections.length === 1 &&
			(loadStorage(selectedHeaderIdKey) === undefined ||
				documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL ||
				documentTypeId === documentTypes.PROFILEV2)
		) {
			onRailChange({detail: {id: formData.headers[0].sections[0].id, level: 'level2', data: formData}});
		} else if (selectedSection) {
			onRailChange({detail: {id: selectedSection.id, level: 'level2', data: formData}});
		} else if (filteredHeader?.isCustom === true) {
			onRailChange({detail: {id: undefined, level: 'level1', data: formData}});
		}
	}, [formData]);

	useEffect(() => {
		if (documentEngagementId !== undefined) {
			if (Number(engagementId) !== Number(documentEngagementId)) {
				setSharedMessageVisible(true);
			}
		}
	}, [documentEngagementId]);

	useEffect(() => {
		if (isFiltered) {
			setSelectedSection(undefined);
		}
		const headerList = getHeaderListForView(formData);
		currentHeader =
			loadStorage(selectedHeaderIdKey) !== undefined
				? headerList.filter((h) => h.value === loadStorage(selectedHeaderIdKey))[0]
				: headerList[0];

		window.addEventListener(clientSideEventNames.showCommentsModal, showComments);
		window.addEventListener(clientSideEventNames.onRailChange, onRailChange);
		window.addEventListener(clientSideEventNames.closeCommentsModal, closeCommentsModal);
		window.addEventListener(clientSideEventNames.showGuidanceModal, openGuidanceModal);
		window.addEventListener(clientSideEventNames.closeGuidanceModal, closeGuidanceModal);
		return () => {
			window.removeEventListener(clientSideEventNames.showCommentsModal, showComments);
			window.removeEventListener(clientSideEventNames.onRailChange, onRailChange);
			window.removeEventListener(clientSideEventNames.closeCommentsModal, closeCommentsModal);
			window.removeEventListener(clientSideEventNames.showGuidanceModal, openGuidanceModal);
			window.removeEventListener(clientSideEventNames.closeGuidanceModal, closeGuidanceModal);
		};
	}, []);

	useEffect(() => {
		if (!userPreferences.isGuestUser && doc?.isSharedExternally) {
			setIsExternallyShared(true);
		}
		if (doc && doc?.documentName && engagement && engagement.engagementDescription) {
			document.title = `${doc?.documentName} | ${engagement.engagementDescription}`;
		}
	}, [doc, engagement]);

	const headerDropdownOptions = [
		{label: labels.edit, onClick: (evt) => handleClickDropdownItem('edit')},
		{label: labels.delete, onClick: (evt) => handleClickDropdownItem('delete')}
	];

	return (
		<Wrapper
			className={`sectionCanvasForm ${documentTypeId === documentTypes.PROFILEV2 ? 'profileSectionCanvasForm' : ''}`}
		>
			{showInfoBanner && displayBanner()}
			<LoadingIndicator show={showIndependenceLoading} styleName="independenceLoader" />
			{submitProfileDetails && submitProfileDetails.isContentUpdated === false ? (
				<>
					<CanvasBanner documentId={documentId} bannerType={canvasFormBannerTypes.profileV2Edit} />
					<CanvasBanner documentId={documentId} bannerType={canvasFormBannerTypes.profileV2} />
				</>
			) : (
				<>
					{sharedMessageVisible === true && (
						<CanvasBanner documentId={documentId} bannerType={canvasFormBannerTypes.shared} />
					)}
					{isExternallyShared && (
						<CanvasBanner documentId={documentId} bannerType={canvasFormBannerTypes.isSharedExternal} />
					)}
					{documentTypeId === documentTypes.PROFILEV2 && (
						<CanvasBanner documentId={documentId} bannerType={canvasFormBannerTypes.profileV2} />
					)}
					{noSectionsAvailable && <section className={`emptyContainer`}>{labels.noSectionsAvailable}</section>}
				</>
			)}
			{!bodyOnlyView && currentHeader && currentHeader.id > 0 && (
				<>
					<section className="SelectedCanvasFormHeader">
						<h2 className="FormTitleHeader">
							<EllipseControl content={currentHeader?.label} tooltip={currentHeader?.label} />
						</h2>
						<Validation
							headerId={currentHeader?.id}
							documentId={documentId}
							displayFor={'header'}
							clearComments={() => {
								setShowIndependenceLoading(true);
								setClearAllComments(true);
							}}
						/>
						<section className="RightSideHeader">
							<section className="custom-labels">
								<span
									className={`edit-icon ${currentHeader?.isCustom ? 'showIcon' : 'hiddenIcon'}`}
									title={labels.custom}
								>
									<Icon src={actionIcBuild24px} />
								</span>
								{currentHeader?.isCustom && (
									<section className="headerOptions">
										<Dropdown
											open={isOpen}
											id={'sectionOption'}
											aria-labelledby={'dropdown-trigger'}
											handleClickOutside={setClickOutside}
											trigger={
												<IconButton
													id={'dropdown-trigger'}
													type="button"
													onClick={handleToggle}
													ariaControls={'sectionOption'}
													ariaExpanded={isOpen}
													ariaLabel="dropdown trigger"
													iconSource={IconoirCustomMoreVerticalAlt}
												/>
											}
											options={headerDropdownOptions}
										/>
									</section>
								)}
								<ConfirmationModal
									show={showModal}
									showButtons
									title={labels.deleteHeader}
									confirmBtnTitle={labels.delete}
									cancelBtnTitle={labels.cancelLabel}
									onOkClick={confirmActionClick}
									onHide={cancel}
									closeTitle={labels.closeLabel}
									modalsize="small"
								>
									<p>{labels.deleteHeaderTitle}</p>
									<p>{currentHeader?.label}</p>
								</ConfirmationModal>
							</section>
						</section>
					</section>
				</>
			)}
			{showSectionList}
			{showEditModal && (
				<>
					<AddEditCustomModal
						afterSave={() => {
							setShowEditModal(false);
							window.dispatchEvent(
								new CustomEvent(clientSideEventNames.onCreateOrEdit, {
									detail: {selectedId: currentHeader?.id, signOffs: signOffs, engagement: {engagementId: engagementId}}
								})
							);
							window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
						}}
						formData={formData}
						entityParentId={currentHeader?.id}
						documentId={documentId}
						engagementId={engagementId}
						entity={'header'}
						entityId={currentHeader?.id}
						entityValue={currentHeader?.label}
						isEdit
						isCustom
						onHide={cancel}
						showTabs={false}
					/>
				</>
			)}
			{showCommentsModal && (
				<DraggableComments show={showCommentsModal} bodyId={bodyId} documentTypeId={commentsDocumentTypeId} />
			)}
			{showGuidanceModal && (
				<DraggableGuidance show={showGuidanceModal} guidanceDetails={guidanceDetails} guidanceId={guidanceId} />
			)}
			{showUnsavedChangesModal && (
				<UnsavedChangesModal
					show={showUnsavedChangesModal}
					onCancel={onUnsavedChangesStay}
					onConfirm={onUnsavedChangesLeave}
				/>
			)}
		</Wrapper>
	);
}

const Wrapper = styled.section`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100%;
	width: 100%;
	position: relative;
	.independenceLoader {
		z-index: 9999;
	}
	.independenceAccessButtons {
		margin-left: auto;
		margin-right: var(--px-6);
		.motif-button {
			margin-top: var(--px-15);
			margin-left: var(--px-10);
			display: inline-flex;
		}
	}
	.SharedFormLabel {
		margin: var(--px-20) var(--px-20) 0 var(--px-20);
		width: calc(100% - var(--px-25));
		box-shadow: 0 0 var(--px-6) 0 rgba(0, 0, 0, 0.2);
		.SharedFormLabelText {
			font-size: var(--px-14);
		}
	}
	.SelectedCanvasFormHeader {
		padding: var(--px-15) var(--px-10) var(--px-15) var(--px-20);
		display: flex;
		align-items: center;
		width: calc(100% - var(--px-3));
		border-bottom: var(--px-1) solid var(--neutrals-300);
		background: var(--neutrals-00white);
		margin-top: var(--px-10);
		@media only screen and (max-width: 1280px) {
			width: calc(100% - var(--px-6));
		}
		.FormTitleHeader {
			font-size: var(--px-20);
			margin: 0;
			display: inline-flex;
			width: calc(100% - var(--px-193));
			padding-right: var(--px-20);
			@media only screen and (max-width: 1280px) {
				width: calc(100% - var(--px-188));
			}
		}
		.header-validation {
			width: var(--px-65);
			margin-right: var(--px-6);
			.motif-dropdown {
				.ValidationIcon {
					.validationcount {
						max-width: var(--px-50);
					}
				}
			}
		}
		.RightSideHeader {
			display: inline-flex;
			margin-left: auto;
			width: var(--px-60);
			justify-content: flex-end;
			margin-right: var(--px-5);
			.custom-labels {
				display: flex;
				align-items: center;
				margin: 0;
				svg {
					width: var(--px-24) !important;
					height: var(--px-24) !important;
					padding: 0;
				}
				.hiddenIcon {
					display: none;
				}
				.showIcon {
					display: inline-block;
				}
				.edit-icon {
					margin-right: var(--px-10);
					height: var(--px-20);
					margin-top: var(--px-3);
				}
				.motif-dropdown {
					height: var(--px-24);
					margin: 0;
				}
			}
			.BadgeTextWrapper + .custom-labels {
				margin-left: var(--px-5);
			}
		}
	}
	.sectionIncompletePadding {
		padding-left: var(--px-30);
	}
	.bodyIncompletePadding {
		padding-left: 0;
	}
	.selectDropdown {
		display: inline-flex;
		width: calc(100% - var(--px-150));
		@media only screen and (max-width: 1280px) {
			width: calc(100% - var(--px-162));
		}
	}
	ul,
	ol {
		padding: 0 0 0 var(--px-24) !important;
		li {
			margin: 0;
			list-style-type: inherit;
			font-weight: 500;
			* {
				margin: 0;
				line-height: var(--px-24) !important;
			}
		}
	}
	&.profileSectionCanvasForm {
		ul,
		ol {
			padding: 0 0 0 var(--px-24) !important;
			list-style-type: disc !important;
			li {
				margin: 0 0 0 var(--px-30);
				list-style-type: inherit;
				font-weight: 500;
				* {
					margin: 0;
					line-height: var(--px-24) !important;
				}
			}
		}
	}
`;
